import React, { useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Upload, Calendar, CheckCircle } from "lucide-react";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import AddSupplierItem from "./AddSupplierItem";
import FormItems from "./FormItems";
import BackBtn from "../../widgets/BackBtn";
import LogoutButton from "../../widgets/LogoutButton";
import SupplierItems from "./SupplierItems";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import useApiEp from "../../hooks/useApiEp"; // <-- 1. Import the hook

const OesDataForm = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // 2. Fetch form items to determine if the list is empty
  const { data: formItems } = useApiEp(`/admin/get-form-items`);

  // Boolean check: true if the array doesn't exist or its length is 0
  const isFormItemsEmpty = !formItems || formItems.length === 0;

  const events = [
    {
      id: "imtex2025",
      name: "IMTEX 2025",
      description: " innovations in manufacturing technology",
    },
    {
      id: "dmtx2025-imtma",
      name: "DMTX 2025 - IMTMA",
      description: "Delhi Machine Tool Expo 2025",
    },
  ];

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleImportClick = () => {
    setShowImportDialog(true);
    setSelectedEvent(null);
    setImportResult(null);
  };

  const handleEventSelect = (eventId) => {
    setSelectedEvent(eventId);
  };

  const handleImportConfirm = async () => {
    if (!selectedEvent) return;

    setLoading(true);
    setImportResult(null);

    try {
      const response = await fetch(`${API_ENDPOINT}/admin/import-oes-forms`, {
        method: "POST",
        ...getHeader(ADMIN),
        body: JSON.stringify({ eventId: selectedEvent }),
      });

      const data = await response.json();

      if (response.ok) {
        setImportResult({
          success: true,
          message: data.message || "Import successful!",
        });
        setTimeout(() => {
          setShowImportDialog(false);
          setImportResult(null);
          document.location.reload();
        }, 2000);
      } else {
        setImportResult({
          success: false,
          message: data.error || "Import failed",
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box maxWidth={1200} sx={{ m: "16px auto", px: { xs: 2, md: 3 }, pb: 5 }}>
        <BackBtn bstyle={{ position: "fixed", left: 16, top: 16, zIndex: 1100 }} />
        <LogoutButton bstyle={{ position: "fixed", top: 16, right: 16, zIndex: 1100 }} />

        <RdMyModal
          showModal={showAddSupplier}
          setShowModal={setShowAddSupplier}
          modalC={<AddSupplierItem />}
        />

        {/* Page Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em" }}>
              Form Names & Suppliers
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5 }}>
              Manage OES forms, expiry dates, field configurations, and supplier entries.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            disabled={!isFormItemsEmpty}
            startIcon={<Upload size={18} />}
            onClick={handleImportClick}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              borderColor: "#E2E8F0",
              bgcolor: "#FFFFFF",
              color: "#475467",
              px: 2,
              py: 1,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            Import from Previous Events
          </Button>
        </Box>

        {/* Styled Tabs */}
        <Paper elevation={0} sx={{ borderRadius: "12px", bgcolor: "#FFFFFF", border: "1px solid #E2E8F0", mb: 3, p: 0.5 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            sx={{
              "& .MuiTabs-indicator": { bgcolor: "#7C3AED", height: 3, borderRadius: 2 },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#64748B",
                borderRadius: "8px",
                py: 1.2,
                px: 3,
                "&.Mui-selected": { color: "#7C3AED" },
              },
            }}
          >
            <Tab label="Form Names" />
            <Tab label="Suppliers" />
          </Tabs>
        </Paper>

        <Box sx={{ pt: 1 }}>
          {tabIndex === 0 && <FormItems />}
          {tabIndex === 1 && <SupplierItems />}
        </Box>

        {/* Import Dialog */}
        <Dialog
          open={showImportDialog}
          onClose={() => !loading && setShowImportDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Upload size={24} />
              Import from Previous Events
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an event to import form names and suppliers from:
            </Typography>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action will replace the current
                form names and suppliers with those from the selected event.
                Existing data cannot be recovered.
              </Typography>
            </Alert>

            {importResult && (
              <Alert
                severity={importResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
              >
                {importResult.message}
              </Alert>
            )}

            <Grid container spacing={2}>
              {events.map((event) => (
                <Grid item xs={12} key={event.id}>
                  <Card
                    variant={
                      selectedEvent === event.id ? "outlined" : "elevation"
                    }
                    sx={{
                      border:
                        selectedEvent === event.id
                          ? "2px solid #1976d2"
                          : "none",
                      backgroundColor:
                        selectedEvent === event.id ? "#e3f2fd" : "inherit",
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleEventSelect(event.id)}
                      disabled={loading}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Calendar size={20} />
                            <Typography variant="h6">{event.name}</Typography>
                          </Box>
                          {selectedEvent === event.id && (
                            <CheckCircle size={20} color="#1976d2" />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {event.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setShowImportDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportConfirm}
              variant="contained"
              disabled={!selectedEvent || loading}
              startIcon={loading && <CircularProgress size={16} />}
            >
              {loading ? "Importing..." : "Import"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default OesDataForm;
